import React, { useEffect, useState, useContext } from "react";
import {
  Tooltip,
  Typography,
  Row,
  Col,
  Button,
  Form,
  Input,
  Spin,
  Collapse,
  Tabs,
  message,
  Modal,
  Switch,
  DatePicker,
  Tag,
} from "antd";
import moment from "moment";
import {
  EditOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  HomeOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import "./index.css";
import CardContainer from "../../components/Card";
import Sidebar from "../../components/Sidebar";
import StoreEditPopup from "./StoreEditPopup";
import TableContainer from "../../components/Table";
import "../../../src/components/Table/index.css";
import { fetchApi, saveApi, getApi, fetchGoFrugalStores, postApiRaw } from "../../helpers";
import { getUserData } from "../../helpers/permissions";
import { StoreContext } from "../../context";

const { Panel } = Collapse;

const ISSUE_FIELD_INFO = {
  auto_refund_limit: "Maximum order value (₹) eligible for an automatic refund. Orders above this amount are escalated to an agent instead of being refunded automatically.",
  escalation_value_threshold: "Hard cap (₹) — any issue where the order total exceeds this value is always sent to an agent, regardless of issue type or other rules.",
  daily_refund_limit: "Maximum number of automatic refunds a single customer can receive in one day. Once hit, further refund requests that day are escalated.",
  monthly_refund_limit: "Maximum number of automatic refunds a single customer can receive in a calendar month. Once hit, further refund requests that month are escalated.",
  coupon_expiry_days: "Number of days before an auto-issued or agent-issued coupon expires. Applies to all coupons created through the issue resolution flow.",
  delay_goodwill_coupon: "Fixed coupon amount (₹) given automatically when a customer reports a delivery delay. This is a goodwill gesture and does not count toward refund limits.",
  coupon_abuse_window_days: "Lookback period (days) used to detect coupon abuse. If a customer receives more auto-coupons than the threshold within this window, they are flagged.",
  coupon_abuse_threshold: "Number of auto-coupons a customer can receive within the abuse window before being flagged. Flagged customers are escalated instead of auto-resolved.",
  repeat_sku_window_days: "Lookback period (days) for detecting repeat complaints on the same product. If a customer complains about the same item again within this window, the issue is escalated.",
  repeat_sku_threshold: "Number of repeat complaints on the same product within the window before the customer is flagged for suspicious activity and the issue is escalated.",
  is_enabled: "Master switch for the auto-resolution engine. When OFF, every issue is sent directly to a human agent — no automatic refunds or coupons are issued.",
};

export default function Settings() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [editStockConfig, setEditStockConfig] = useState(false);
  const [editDeliveryConfig, setEditDeliveryConfig] = useState(false);
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState("1");

  // Store state
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSidebar, setStoreSidebar] = useState(false);
  const [selectedStore, setSelectedStore] = useState({});
  const [editStorePopup, setEditStorePopup] = useState(false);
  const [storePopupMode, setStorePopupMode] = useState("edit");
  const [savingStore, setSavingStore] = useState(false);
  const [gofrugalStoreOptions, setGofrugalStoreOptions] = useState([]);
  const [loadingGofrugalStores, setLoadingGofrugalStores] = useState(false);
  const [storeForm] = Form.useForm();
  const [onboardingActive, setOnboardingActive] = useState(false);
  const [onboardingSteps, setOnboardingSteps] = useState([]);

  const [savedConfigPayload, setSavedConfigPayload] = useState({});

  const [issueConfig, setIssueConfig] = useState({});
  const [editIssueConfig, setEditIssueConfig] = useState(false);
  const [loadingIssueConfig, setLoadingIssueConfig] = useState(true);
  const [issueForm] = Form.useForm();

  const [maintenanceConfig, setMaintenanceConfig] = useState({});
  const [editMaintenance, setEditMaintenance] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  const [endingMaintenance, setEndingMaintenance] = useState(false);
  const [maintenanceForm] = Form.useForm();

  useEffect(() => {
    getConfig(savedConfigPayload);
    getIssueConfig();
  }, [storeId]);

  async function getConfig(payload = {}) {
    let configPayload = { ...payload };
    if (storeId) {
      configPayload = { ...configPayload, store_id: storeId };
    } else {
      delete configPayload.store_id;
    }
    const res = await fetchApi("getConfig", "ecomm/");
    if (res.in_stock) {
      setConfig(res);
    }
    setEditDeliveryConfig(false);
    setEditStockConfig(false);
    setLoading(false);
    setSavedConfigPayload(configPayload);
  }


  async function getIssueConfig() {
    setLoadingIssueConfig(true);
    const res = await postApiRaw({ key: "JWT", secret: "RAPSAP" }, "issue/getIssueConfig", "ecomm/");
    if (res?.success && res?.data) {
      setIssueConfig(res.data);
    }
    setEditIssueConfig(false);
    setLoadingIssueConfig(false);
  }

  async function updateIssueConfig() {
    if (!editIssueConfig) return;
    setLoadingIssueConfig(true);
    issueForm
      .validateFields()
      .then(async (values) => {
        const payload = {
          key: "JWT",
          secret: "RAPSAP",
          ...values,
          is_enabled: values.is_enabled ? 1 : 0,
        };
        const res = await postApiRaw(payload, "issue/updateIssueConfig", "ecomm/");
        if (res?.success) {
          message.success("Issue Resolution Config Updated Successfully.");
          if (res.data) setIssueConfig(res.data);
          setEditIssueConfig(false);
        } else {
          message.error(res?.msg || "Something went wrong, please try again.");
        }
        setLoadingIssueConfig(false);
      })
      .catch(() => {
        setLoadingIssueConfig(false);
      });
  }

  function normalizeMaintenanceData(data) {
    return (Array.isArray(data) ? data[0] : data) || {};
  }

  async function getMaintenanceMode() {
    setLoadingMaintenance(true);
    const res = await postApiRaw({ key: "JWT", secret: "RAPSAP" }, "getMaintenanceMode");
    if (res?.success) {
      setMaintenanceConfig(normalizeMaintenanceData(res.data));
    } else {
      message.error(res?.msg || "Failed to load maintenance mode status.");
    }
    setEditMaintenance(false);
    setLoadingMaintenance(false);
  }

  async function saveMaintenanceMode() {
    if (!editMaintenance) return;
    setLoadingMaintenance(true);
    maintenanceForm
      .validateFields()
      .then(async (values) => {
        const payload = {
          key: "JWT",
          secret: "RAPSAP",
          message: values.message,
          starts_at: values.starts_at.format("YYYY-MM-DD HH:mm:ss"),
          ends_at: values.ends_at.format("YYYY-MM-DD HH:mm:ss"),
          is_active: values.is_active ? 1 : 0,
        };
        const res = maintenanceConfig?.id
          ? await postApiRaw({ ...payload, id: maintenanceConfig.id }, "updateMaintenanceMode")
          : await postApiRaw({ ...payload, created_by: getUserData()?.id }, "createMaintenanceMode");
        if (res?.success) {
          message.success(res?.msg || "Maintenance window saved successfully.");
          setMaintenanceConfig(normalizeMaintenanceData(res.data));
          setEditMaintenance(false);
        } else {
          message.error(res?.msg || "Something went wrong, please try again.");
        }
        setLoadingMaintenance(false);
      })
      .catch(() => {
        setLoadingMaintenance(false);
      });
  }

  async function endMaintenanceNow() {
    setEndingMaintenance(true);
    const res = await postApiRaw({ key: "JWT", secret: "RAPSAP", is_active: 0 }, "setMaintenanceMode");
    if (res?.success) {
      message.success(res?.msg || "Maintenance mode ended.");
      await getMaintenanceMode();
    } else {
      message.error(res?.msg || "Something went wrong, please try again.");
    }
    setEndingMaintenance(false);
  }

  function updateConfig() {
    if (editStockConfig || editDeliveryConfig) {
      setLoading(true);
      const msg = editStockConfig ? "Stock" : "Delivery";
      form
        .validateFields()
        .then(async (values) => {
          let configPayload = { ...config, ...values };
          if (storeId) {
            configPayload = { ...configPayload, store_id: storeId };
          } else {
            delete configPayload.store_id;
          }
          const res = await saveApi(
            configPayload,
            msg + " Config Updated",
            "updateConfig"
          );
          if (res) {
            getConfig(savedConfigPayload);
          } else {
            setLoading(false);
          }
        })
        .catch((info) => {
          setLoading(false);
          console.log("Validate Failed:", info);
        });
    }
  }

  const getStores = async () => {
    setLoadingStores(true);
    const res = await getApi({ "type": "admin" }, "getStores", "dashboard/");
    if (res.data && res.data.length) setStores(res.data);
    setLoadingStores(false);
  };

  const handleTabChange = (key) => {
    setActiveKey(key);
    if (key === "2") {
      getStores();
    }
    if (key === "3") {
      getMaintenanceMode();
    }
  };

  const openEditStorePopup = () => {
    setStorePopupMode("edit");
    const initialPincodes = Array.isArray(selectedStore?.pincodes) && selectedStore.pincodes.filter(Boolean).length
      ? selectedStore.pincodes.filter(Boolean).map((item) => ({
        id: item.id,
        pincode: item.pincode || "",
        description: item.description || "",
      }))
      : [{ pincode: "", description: "" }];

    storeForm.setFieldsValue({
      name: selectedStore.name,
      description: selectedStore.description,
      code: selectedStore.code,
      contact_no: selectedStore.contact_no,
      lat: selectedStore.lat,
      lng: selectedStore.lng ?? selectedStore.long,
      radius: selectedStore.radius,
      pincodes: initialPincodes,
    });
    setEditStorePopup(true);
  };

  const openCreateStorePopup = () => {
    setStorePopupMode("create");
    loadGofrugalStores();
    storeForm.setFieldsValue({
      name: "",
      description: "",
      code: "",
      contact_no: "",
      lat: undefined,
      lng: undefined,
      radius: undefined,
      gofrugal_outlet_id: undefined,
      pincodes: [{ pincode: "", description: "" }],
    });
    setEditStorePopup(true);
  };

  const loadGofrugalStores = async () => {
    if (gofrugalStoreOptions.length) {
      return;
    }
    setLoadingGofrugalStores(true);
    const storesData = await fetchGoFrugalStores();
    const options = Array.isArray(storesData)
      ? storesData
        .filter((store) => store?.id && store?.name)
        .map((store) => ({
          value: Number(store.id),
          label: store.name,
        }))
      : [];
    setGofrugalStoreOptions(options);
    setLoadingGofrugalStores(false);
  };

  const initialOnboardingSteps = () => [
    { title: "Creating Store & Saving Pincodes", status: "wait", description: "Waiting..." },
    { title: "Syncing to Rapsap Operations", status: "wait", description: "Waiting..." },
    { title: "Importing GoFrugal Stock", status: "wait", description: "Waiting..." },
  ];

  const updateStep = (index, updates) => {
    setOnboardingSteps(prev => prev.map((step, i) => i === index ? { ...step, ...updates } : step));
  };

  const closeEditStorePopup = () => {
    setEditStorePopup(false);
    setOnboardingActive(false);
    setOnboardingSteps([]);
    storeForm.resetFields();
  };

  const confirmStoreAction = (isCreateMode) =>
    new Promise((resolve) => {
      Modal.confirm({
        title: isCreateMode
          ? "Do you want to create this store?"
          : "Do you want to edit this store?",
        okText: "OK",
        cancelText: "Cancel",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleSaveStore = async () => {
    const isCreateMode = storePopupMode === "create";
    const confirmed = await confirmStoreAction(isCreateMode);
    if (!confirmed) {
      return;
    }

    try {
      const values = await storeForm.validateFields();
      setSavingStore(true);

      const normalizedPincodes = Array.isArray(values.pincodes)
        ? values.pincodes
          .filter((item) => item && item.pincode)
          .map((item, index) => ({
            id: item.id || index + 1,
            pincode: item.pincode,
            description: item.description,
          }))
        : [];

      const basePayload = {
        name: values.name,
        code: values.code,
        description: values.description,
        contact_no: values.contact_no,
        lat:
          values.lat === undefined || values.lat === null || values.lat === ""
            ? null
            : Number(values.lat),
        long:
          values.lng === undefined || values.lng === null || values.lng === ""
            ? null
            : Number(values.lng),
        radius:
          values.radius === undefined || values.radius === null || values.radius === ""
            ? null
            : Number(values.radius),
        pincodes: normalizedPincodes.map((item) => ({
          pincode: item.pincode,
          description: item.description,
        })),
      };

      // ---- EDIT MODE: keep existing behavior ----
      if (!isCreateMode) {
        const apiPayload = {
          ...basePayload,
          store_id: selectedStore.store_id,
          gofrugal_outlet_id: selectedStore.gofrugal_outlet_id,
        };

        const res = await getApi(apiPayload, "updateStore", "dashboard/");
        if (!res?.success) {
          return;
        }

        const payload = {
          ...selectedStore,
          ...values,
          long: values.lng,
          pincodes: normalizedPincodes,
        };

        setStores((prevStores) =>
          prevStores.map((store) =>
            store.store_id === selectedStore.store_id ? payload : store
          )
        );
        setSelectedStore(payload);
        message.success(res?.msg || "Store updated successfully");
        closeEditStorePopup();
        return;
      }

      // ---- CREATE MODE: onboarding flow ----
      const apiPayload = {
        ...basePayload,
        gofrugal_outlet_id: Number(values.gofrugal_outlet_id),
      };

      // Switch to onboarding view
      setOnboardingSteps(initialOnboardingSteps());
      setOnboardingActive(true);

      // Step 1 & 2: createStore API (creates store + syncs to Rapsap Operations)
      updateStep(0, { status: "process", description: "Creating store in database..." });

      const res = await postApiRaw(apiPayload, "createStore", "dashboard/");
      const steps = res?.data?.steps || {};
      const storeId = res?.data?.store_id;

      // Update Step 1 result
      if (steps.store_created?.success) {
        updateStep(0, { status: "finish", description: steps.store_created.msg });
      } else {
        updateStep(0, { status: "error", description: steps.store_created?.msg || res?.msg || "Store creation failed" });
        updateStep(1, { status: "error", description: "Skipped" });
        updateStep(2, { status: "error", description: "Skipped" });
        return;
      }

      // Update Step 2 result (Rapsap Operations sync — already done server-side)
      if (steps.rapid_sync?.success) {
        updateStep(1, { status: "finish", description: steps.rapid_sync.msg });
      } else {
        updateStep(1, { status: "error", description: steps.rapid_sync?.msg || "Rapsap Operations sync failed" });
      }

      // Step 3: Import GoFrugal Stock (separate API call)
      updateStep(2, { status: "process", description: "Initiating stock import..." });

      const syncRes = await postApiRaw(
        { store_id: storeId, gofrugal_outlet_id: Number(values.gofrugal_outlet_id) },
        "initialStoreSync",
        "dashboard/"
      );

      if (syncRes?.success) {
        updateStep(2, { status: "finish", description: syncRes.msg || "Stock sync initiated successfully" });
      } else {
        updateStep(2, { status: "error", description: syncRes?.msg || "Failed to initiate stock sync" });
      }

      // Refresh store list
      getStores();
      // Reset gofrugal options so they reload next time (new store should be filtered)
      setGofrugalStoreOptions([]);

    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error("Unable to create store.");
      console.log("Store creation failed:", error);
    } finally {
      setSavingStore(false);
    }
  };

  const handleRetryOnboarding = () => {
    setOnboardingActive(false);
    setOnboardingSteps([]);
  };

  const storeColumns = [
    {
      title: "Store ID",
      dataIndex: "store_id",
      key: "store_id",
      sorter: true,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Contact No.",
      dataIndex: "contact_no",
      key: "contact_no",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (val) => (
        <span className={`store-status-badge ${val === 1 ? "active" : "inactive"}`}>
          {val === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const serviceablePincodes = Array.isArray(selectedStore?.pincodes)
    ? selectedStore.pincodes.filter(Boolean)
    : [];

  return (
    <div className="card-container">
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        destroyInactiveTabPane={true}
        type="card"
      >
        <Tabs.TabPane tab="Config" key="1">
          <CardContainer bordered={false} hoverable={false}>
            <div className="breadcrumb-search-row">
              <div className="breadcrumb-container">
                <div className="breadcrumb-content">
                  <HomeOutlined /> <span className="breadcrumb-title"> / Config Settings</span>
                </div>
              </div>
              <div className="search-controls"></div>
            </div>
            <Collapse
              style={{ marginTop: 15 }}
              ghost
              defaultActiveKey={["1", "2", "3"]}
            >
              <Panel
                collapsible="disabled"
                className="pnld"
                showArrow={false}
                header={
                  <Row>
                    <Col span={20}>
                      <Typography.Title level={5}>
                        Stock Config
                        <Tooltip
                          overlayClassName="tltp"
                          placement="right"
                          title="These configuration ranges help you show your inventory stock status according to the count you decide. Eg. range from 1-10 can show as 'finishing soon' as a product status You can customise these as per your requirements."
                        >
                          <InfoCircleOutlined style={{ marginLeft: 6 }} />
                        </Tooltip>
                      </Typography.Title>
                    </Col>
                    <Col style={{ textAlign: "right", marginTop: 5 }} span={4}>
                      {!storeId && (
                        <Button
                          size="small"
                          onClick={() => (setEditStockConfig(true), updateConfig())}
                          type="primary"
                          disabled={editDeliveryConfig || editIssueConfig}
                        >
                          {editStockConfig ? <SaveOutlined /> : <EditOutlined />}
                          {editStockConfig ? "Save" : "Edit"}
                        </Button>
                      )}
                    </Col>
                  </Row>
                }
                key="1"
              >
                <Spin className="loadr" spinning={loading}>
                  <Form
                    requiredMark={false}
                    form={form}
                    layout="vertical"
                    name="stockConfig"
                  >
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Out Of Stock
                        </Typography.Text>
                        <br />
                        {editStockConfig ? (
                          <Form.Item
                            initialValue={config.out_of_stock}
                            name="out_of_stock"
                            key="out_of_stock"
                            extra="It should be always 0."
                          >
                            <Input readOnly placeholder="Ex. 0" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            0 -{" "}
                            {config.finishing_soon ? config.finishing_soon - 1 : 0}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Finishing Soon
                        </Typography.Text>
                        <br />
                        {editStockConfig ? (
                          <Form.Item
                            initialValue={config.finishing_soon}
                            name="finishing_soon"
                            key="finishing_soon"
                            // hasFeedback
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (parseInt(value)) {
                                    if (value > config.out_of_stock) {
                                      if (
                                        parseInt(value).toString().length <
                                        value.length
                                      ) {
                                        return Promise.reject(
                                          new Error("It should be an integer !")
                                        );
                                      }
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error("It should be a greater than 0 !")
                                    );
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 5" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            {config.finishing_soon ? config.finishing_soon : 0} -{" "}
                            {config.in_stock ? config.in_stock - 1 : 0}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">In Stock</Typography.Text>
                        <br />
                        {editStockConfig ? (
                          <Form.Item
                            initialValue={config.in_stock}
                            name="in_stock"
                            key="in_stock"
                            // hasFeedback
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (parseInt(value)) {
                                    if (value > config.finishing_soon) {
                                      if (
                                        parseInt(value).toString().length <
                                        value.length
                                      ) {
                                        return Promise.reject(
                                          new Error("It should be an integer !")
                                        );
                                      }
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error(
                                        "It should be greater than " +
                                        config.finishing_soon +
                                        "!"
                                      )
                                    );
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 20" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            {config.in_stock ? config.in_stock : 0} -{" "}
                            {config.plenty ? config.plenty - 1 : 0}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">Plenty</Typography.Text>
                        <br />
                        {editStockConfig ? (
                          <Form.Item
                            initialValue={config.plenty}
                            name="plenty"
                            key="plenty"
                            // hasFeedback
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (parseInt(value)) {
                                    if (value > config.in_stock) {
                                      if (
                                        parseInt(value).toString().length <
                                        value.length
                                      ) {
                                        return Promise.reject(
                                          new Error("It should be an integer !")
                                        );
                                      }
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error(
                                        "It should be greater than " +
                                        config.in_stock +
                                        "!"
                                      )
                                    );
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 200" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            {config.plenty ? "> " + config.plenty : "> 0"}
                          </Typography.Text>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </Spin>
              </Panel>
              <Panel
                className="pnld"
                collapsible="disabled"
                showArrow={false}
                header={
                  <Row>
                    <Col span={20}>
                      <Typography.Title level={5}>
                        Delivery Config
                        <Tooltip
                          overlayClassName="tltp"
                          placement="right"
                          title="These configuration help to place an order according the values set by you. Orders above minimum order value would be placed with delivery fee. Above the delivery threshold value the order would be placed with free delivery. You can customise these as per your requirements."
                        >
                          <InfoCircleOutlined style={{ marginLeft: 6 }} />
                        </Tooltip>
                      </Typography.Title>
                    </Col>
                    <Col style={{ textAlign: "right", marginTop: 5 }} span={4}>
                      {!storeId && (
                        <Button
                          size="small"
                          onClick={() => (
                            updateConfig(), setEditDeliveryConfig(true)
                          )}
                          type="primary"
                          disabled={editStockConfig || editIssueConfig}
                        >
                          {editDeliveryConfig ? <SaveOutlined /> : <EditOutlined />}
                          {editDeliveryConfig ? "Save" : "Edit"}
                        </Button>
                      )}
                    </Col>
                  </Row>
                }
                key="2"
              >
                <Spin className="loadr" spinning={loading}>
                  <Form
                    requiredMark={false}
                    form={form}
                    layout="vertical"
                    name="stockConfig"
                  >
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Min Order Value
                        </Typography.Text>
                        <br />
                        {editDeliveryConfig ? (
                          <Form.Item
                            initialValue={config.min_order_value}
                            name="min_order_value"
                            key="min_order_value"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value > 0) {
                                    if (
                                      parseFloat(value).toFixed(2).length <
                                      value.length
                                    ) {
                                      return Promise.reject(
                                        new Error("Numbers upto 2 decimal places")
                                      );
                                    }
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 299" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            ₹ {config.min_order_value ? config.min_order_value : 0}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Delivery Fee Threshold
                        </Typography.Text>
                        <br />
                        {editDeliveryConfig ? (
                          <Form.Item
                            initialValue={config.delivery_fee_threshold}
                            name="delivery_fee_threshold"
                            key="delivery_fee_threshold"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value > 0) {
                                    if (
                                      parseFloat(value).toFixed(2).length <
                                      value.length
                                    ) {
                                      return Promise.reject(
                                        new Error("Numbers upto 2 decimal places")
                                      );
                                    }
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 50" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            ₹{" "}
                            {config.delivery_fee_threshold
                              ? config.delivery_fee_threshold
                              : 0}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Delivery Fee
                        </Typography.Text>
                        <br />
                        {editDeliveryConfig ? (
                          <Form.Item
                            initialValue={config.delivery_fee}
                            name="delivery_fee"
                            key="delivery_fee"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value > 0) {
                                    if (
                                      parseFloat(value).toFixed(2).length <
                                      value.length
                                    ) {
                                      return Promise.reject(
                                        new Error("Numbers upto 2 decimal places")
                                      );
                                    }
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error("It should be a number !")
                                  );
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Ex. 20" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            ₹ {config.delivery_fee ? config.delivery_fee : 0}
                          </Typography.Text>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </Spin>
              </Panel>
              <Panel
                className="pnld"
                collapsible="disabled"
                showArrow={false}
                header={
                  <Row>
                    <Col span={20}>
                      <Typography.Title level={5}>
                        Issue Resolution Config
                        <Tooltip
                          overlayClassName="tltp"
                          placement="right"
                          title="Configure rules for how customer issues are automatically resolved, including refund limits, coupon settings, and abuse detection thresholds."
                        >
                          <InfoCircleOutlined style={{ marginLeft: 6 }} />
                        </Tooltip>
                      </Typography.Title>
                    </Col>
                    <Col style={{ textAlign: "right", marginTop: 5 }} span={4}>
                      <Button
                        size="small"
                        onClick={() => (setEditIssueConfig(true), updateIssueConfig())}
                        type="primary"
                        disabled={editStockConfig || editDeliveryConfig}
                      >
                        {editIssueConfig ? <SaveOutlined /> : <EditOutlined />}
                        {editIssueConfig ? "Save" : "Edit"}
                      </Button>
                    </Col>
                  </Row>
                }
                key="3"
              >
                <Spin className="loadr" spinning={loadingIssueConfig}>
                  <Form
                    requiredMark={false}
                    form={issueForm}
                    layout="vertical"
                    name="issueConfig"
                  >
                    <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Auto Refund Limit{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.auto_refund_limit}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.auto_refund_limit}
                            name="auto_refund_limit"
                            rules={[{ validator: (_, value) => value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive number")) }]}
                          >
                            <Input placeholder="Ex. 300" prefix="₹" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>₹ {issueConfig.auto_refund_limit || 0}</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Escalation Value Threshold{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.escalation_value_threshold}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.escalation_value_threshold}
                            name="escalation_value_threshold"
                            rules={[{ validator: (_, value) => value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive number")) }]}
                          >
                            <Input placeholder="Ex. 300" prefix="₹" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>₹ {issueConfig.escalation_value_threshold || 0}</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Daily Refund Limit{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.daily_refund_limit}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.daily_refund_limit}
                            name="daily_refund_limit"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 1" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.daily_refund_limit || 0}</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Monthly Refund Limit{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.monthly_refund_limit}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.monthly_refund_limit}
                            name="monthly_refund_limit"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 2" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.monthly_refund_limit || 0}</Typography.Text>
                        )}
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Coupon Expiry Days{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.coupon_expiry_days}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.coupon_expiry_days}
                            name="coupon_expiry_days"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 7" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.coupon_expiry_days || 0} days</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Delay Goodwill Coupon{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.delay_goodwill_coupon}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.delay_goodwill_coupon}
                            name="delay_goodwill_coupon"
                            rules={[{ validator: (_, value) => value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive number")) }]}
                          >
                            <Input placeholder="Ex. 19" prefix="₹" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>₹ {issueConfig.delay_goodwill_coupon || 0}</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Coupon Abuse Window{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.coupon_abuse_window_days}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.coupon_abuse_window_days}
                            name="coupon_abuse_window_days"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 60" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.coupon_abuse_window_days || 0} days</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Coupon Abuse Threshold{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.coupon_abuse_threshold}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.coupon_abuse_threshold}
                            name="coupon_abuse_threshold"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 3" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.coupon_abuse_threshold || 0}</Typography.Text>
                        )}
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Repeat SKU Window{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.repeat_sku_window_days}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.repeat_sku_window_days}
                            name="repeat_sku_window_days"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 30" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.repeat_sku_window_days || 0} days</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Repeat SKU Threshold{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.repeat_sku_threshold}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item
                            initialValue={issueConfig.repeat_sku_threshold}
                            name="repeat_sku_threshold"
                            rules={[{ validator: (_, value) => parseInt(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Must be a positive integer")) }]}
                          >
                            <Input placeholder="Ex. 2" />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{issueConfig.repeat_sku_threshold || 0}</Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">
                          Auto-Resolution Engine{" "}
                          <Tooltip overlayClassName="tltp" placement="right" title={ISSUE_FIELD_INFO.is_enabled}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typography.Text>
                        <br />
                        {editIssueConfig ? (
                          <Form.Item name="is_enabled" valuePropName="checked" initialValue={issueConfig.is_enabled === 1}>
                            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                          </Form.Item>
                        ) : (
                          <span className={`store-status-badge ${issueConfig.is_enabled === 1 ? "active" : "inactive"}`}>
                            {issueConfig.is_enabled === 1 ? "Enabled" : "Disabled"}
                          </span>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </Spin>
              </Panel>
              {/* <Panel
          className="pnld"
          collapsible="disabled"
          showArrow={false}
          header={
            <Row>
              <Col span={20}>
                <Typography.Title level={5}>
                  New In Stock
                  <Tooltip
                    overlayClassName="tltp"
                    placement="right"
                    title="These configuration ranges help you show your inventory stock as new for the count of days you decide. Eg. 7 will show your product for 7 days as 'new in stock' You can customise these as per your requirements."
                  >
                    <InfoCircleOutlined style={{ marginLeft: 6 }} />
                  </Tooltip>
                </Typography.Title>
              </Col>
              <Col style={{ textAlign: "right", marginTop: 5 }} span={4}>
                <Button size="small" disabled type="primary">
                  <EditOutlined /> Edit
                </Button>
              </Col>
            </Row>
          }
          key="3"
        >
          <Typography.Text strong>7 days</Typography.Text>
        </Panel> */}
            </Collapse>
          </CardContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Stores" key="2">
          <CardContainer bordered={false} hoverable={false}>
            <div className="hide-search">
              <TableContainer
                breadcrumbTitle="Store Management"
                loading={loadingStores}
                columns={storeColumns}
                data={stores}
                total={stores.length}
                searchFor="Store Name, Code"
                functionsHandler={getStores}
                customFilterComponent={
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateStorePopup}>
                    Create Store
                  </Button>
                }
                rowClickHandler={(record) => {
                  setSelectedStore(record);
                  setStoreSidebar(true);
                }}
                rowKey={(store) => store.store_id}
              />
            </div>
          </CardContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Maintenance Mode" key="3">
          <CardContainer bordered={false} hoverable={false}>
            <div className="breadcrumb-search-row">
              <div className="breadcrumb-container">
                <div className="breadcrumb-content">
                  <HomeOutlined /> <span className="breadcrumb-title"> / Maintenance Mode</span>
                </div>
              </div>
              <div className="search-controls"></div>
            </div>
            <Collapse style={{ marginTop: 15 }} ghost defaultActiveKey={["1"]}>
              <Panel
                collapsible="disabled"
                className="pnld"
                showArrow={false}
                header={
                  <Row>
                    <Col span={14}>
                      <Typography.Title level={5}>
                        Maintenance Window
                        <Tooltip
                          overlayClassName="tltp"
                          placement="right"
                          title="Configure the maintenance window shown to mobile users. Only one window can be active at a time."
                        >
                          <InfoCircleOutlined style={{ marginLeft: 6 }} />
                        </Tooltip>
                      </Typography.Title>
                    </Col>
                    <Col style={{ textAlign: "right", marginTop: 5 }} span={10}>
                      {maintenanceConfig?.is_active === 1 && (
                        <Button
                          size="small"
                          danger
                          style={{ marginRight: 8 }}
                          disabled={editMaintenance}
                          loading={endingMaintenance}
                          onClick={endMaintenanceNow}
                        >
                          End Maintenance Now
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => (setEditMaintenance(true), saveMaintenanceMode())}
                        type="primary"
                      >
                        {editMaintenance ? <SaveOutlined /> : <EditOutlined />}
                        {editMaintenance ? "Save" : "Edit"}
                      </Button>
                    </Col>
                  </Row>
                }
                key="1"
              >
                <Spin className="loadr" spinning={loadingMaintenance}>
                  <Form
                    requiredMark={false}
                    form={maintenanceForm}
                    layout="vertical"
                    name="maintenanceConfig"
                  >
                    <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
                      <Col xs={24}>
                        <Typography.Text type="secondary">
                          Message shown to mobile users
                        </Typography.Text>
                        <br />
                        {editMaintenance ? (
                          <Form.Item
                            initialValue={maintenanceConfig.message}
                            name="message"
                            rules={[{ required: true, message: "Message is required" }]}
                          >
                            <Input.TextArea rows={2} placeholder="Ex. We're upgrading Prodesk. Back shortly." />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>{maintenanceConfig.message || "Not configured"}</Typography.Text>
                        )}
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">Starts At</Typography.Text>
                        <br />
                        {editMaintenance ? (
                          <Form.Item
                            initialValue={maintenanceConfig.starts_at ? moment(maintenanceConfig.starts_at) : undefined}
                            name="starts_at"
                            rules={[{ required: true, message: "Start time is required" }]}
                          >
                            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: "100%" }} />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            {maintenanceConfig.starts_at ? moment(maintenanceConfig.starts_at).format("DD-MM-YYYY HH:mm:ss") : "-"}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">Ends At</Typography.Text>
                        <br />
                        {editMaintenance ? (
                          <Form.Item
                            initialValue={maintenanceConfig.ends_at ? moment(maintenanceConfig.ends_at) : undefined}
                            name="ends_at"
                            rules={[{ required: true, message: "End time is required" }]}
                          >
                            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: "100%" }} />
                          </Form.Item>
                        ) : (
                          <Typography.Text strong>
                            {maintenanceConfig.ends_at ? moment(maintenanceConfig.ends_at).format("DD-MM-YYYY HH:mm:ss") : "-"}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} md={12} lg={6}>
                        <Typography.Text type="secondary">Active</Typography.Text>
                        <br />
                        {editMaintenance ? (
                          <Form.Item
                            initialValue={maintenanceConfig.is_active === 1}
                            name="is_active"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        ) : (
                          <Tag color={maintenanceConfig.is_active === 1 ? "green" : "default"}>
                            {maintenanceConfig.is_active === 1 ? "Active" : "Inactive"}
                          </Tag>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </Spin>
              </Panel>
            </Collapse>
          </CardContainer>
        </Tabs.TabPane>
      </Tabs>

      {storeSidebar && (
        <Sidebar
          title="Store Details"
          visible={storeSidebar}
          onClose={() => {
            setStoreSidebar(false);
            closeEditStorePopup();
          }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={openEditStorePopup}
              >
                Edit
              </Button>
            </div>
          }
        >
          <div className="settings-store-sidebar-view">
            <Row gutter={[0, 0]}>
              <Col xs={24} md={12} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Store ID
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.store_id || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} md={12} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Store Name
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.name || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} md={12} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Code
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.code || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} md={12} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Contact No.
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.contact_no || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Description
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value settings-store-description">
                  {selectedStore.description || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} className="settings-store-subheading-wrap">
                <Typography.Text className="settings-store-subheading" type="secondary">
                  Location Details
                </Typography.Text>
              </Col>

              <Col xs={24} md={8} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Latitude
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.lat || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} md={8} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Longitude
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.lng || "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} md={8} className="lblc settings-store-info-cell">
                <Typography.Text className="settings-store-label" type="secondary">
                  Radius
                </Typography.Text>
                <br />
                <Typography.Text className="settings-store-value">
                  {selectedStore.radius !== undefined ? selectedStore.radius : "N/A"}
                </Typography.Text>
              </Col>

              <Col xs={24} className="settings-store-subheading-wrap">
                <Typography.Text className="settings-store-subheading" type="secondary">
                  Serviceable Pin Code
                </Typography.Text>
              </Col>

              <Col xs={24} className="settings-store-info-cell settings-store-pincode-wrap">
                {serviceablePincodes.length ? (
                  <table className="settings-store-pincode-table">
                    <thead>
                      <tr>
                        <th>Pin Code</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceablePincodes.map((item, index) => (
                        <tr key={item.id || `${item.pincode}-${index}`}>
                          <td>{item.pincode || "N/A"}</td>
                          <td>{item.description || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Typography.Text className="settings-store-value">N/A</Typography.Text>
                )}
              </Col>
            </Row>

            <div className="settings-store-status-row">
              <div>
                <Typography.Text className="settings-store-label" type="secondary" style={{ marginRight: 8 }}>
                  Store Status
                </Typography.Text>
                <span className={`store-status-badge ${selectedStore.is_active === 1 ? "active" : "inactive"}`}>
                  {selectedStore.is_active === 1 ? "Active" : "Inactive"}
                </span>
              </div>
              <Switch
                checked={selectedStore.is_active === 1}
                onChange={async (checked) => {
                  const res = await getApi(
                    { store_id: selectedStore.store_id, is_active: checked ? 1 : 0 },
                    "updateStore",
                    "dashboard/"
                  );
                  if (res?.success) {
                    setSelectedStore({ ...selectedStore, is_active: checked ? 1 : 0 });
                    getStores();
                    message.success(`Store ${checked ? "activated" : "deactivated"} successfully`);
                  }
                }}
              />
            </div>
          </div>
        </Sidebar>
      )}

      <StoreEditPopup
        visible={editStorePopup}
        loading={savingStore}
        form={storeForm}
        isCreateMode={storePopupMode === "create"}
        gofrugalStoreOptions={gofrugalStoreOptions}
        loadingGofrugalStores={loadingGofrugalStores}
        onCancel={closeEditStorePopup}
        onSave={handleSaveStore}
        onboardingActive={onboardingActive}
        onboardingSteps={onboardingSteps}
        onRetry={handleRetryOnboarding}
      />
    </div>
  );
}
